import React, { useState, useContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { getIn, useFormikContext } from 'formik';
import { useStore } from 'core/store/store.mobx';

import { getPolicyType } from 'core/helpers/policy-type';
import useSession from 'core/hooks/use-session';
import Section from 'core/components/section';
import RepeatField from 'core/components/repeat-field';

function FormList({
  item: Item,
  addForm: AddForm,
  addLabel,
  title,
  disabled,
  id,
  secondaryId,
  singular,
  plural,
  children,
  type,
  classes,
  hasAuto,
  fromPolicy,
  inOffer,
  sppLimits,
  sectionTooltip,
  onAdd,
  showRightLabel,
  notification,
  ...rest
}) {
  const [folded, setFolded] = useState([]);
  const [indexToRemove, setIndexToRemove] = useState(-1);
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  const list = useMemo(() => {
    if (secondaryId) {
      // allow a secondary id so we can display items from multiple lists, mostly useful for PIP when we need to show drivers and nonDrivers
      return (getIn(values, secondaryId) ?? []).concat(getIn(values, id) ?? []);
    }
    return getIn(values, id) ?? [];
  }, [values, id, secondaryId]);

  const toggleFold = (i, foldedStatus) =>
    setFolded((prevFolded) => [...prevFolded.slice(0, i), foldedStatus, ...prevFolded.slice(i + 1, prevFolded.length)]);

  const {
    offer: offerStore,
    account: {
      policies: { policy: policyStore }
    }
  } = useStore();

  const { policy } = policyStore;
  const { isAuto } = getPolicyType(policy?.policyType);

  const session = useSession();

  const allowLicensedActions = fromPolicy
    ? policyStore?.getAllowLicensedActions(session)
    : offerStore?.getIsLicensedForState(session);

  const pushItem = (val) => {
    const item = val;

    Object.keys(item).forEach((key) => {
      if (item[key] === '') {
        item[key] = null;
      }
    });

    // if we have a secondary id, we need to ensure we are only adding the new item to the first id/node, since the list contains both the id and the secondary id values
    const listToUpload = secondaryId ? (values[id] ?? []) : list;

    setFieldValue(id, [...listToUpload, item]);
    setFieldTouched(id);

    if (onAdd) {
      onAdd(item);
    }
  };

  useEffect(() => {
    if (indexToRemove !== -1) {
      const newList = list.slice();
      newList.splice(indexToRemove, 1);
      setFolded([...folded.slice(0, indexToRemove), ...folded.slice(indexToRemove + 1)]);
      setFieldValue(id, newList);
      setFieldTouched(id);
      setIndexToRemove(-1);
    }
  }, [list, indexToRemove, folded, setFieldValue, setFieldTouched, id]);

  return (
    <Section
      className={classes.container}
      title={title}
      type={type || 'SubSection'}
      rightLabel="Total"
      rightValue={`${list.length} ${list.length === 1 ? singular : plural}`}
      id={id}
      titleTooltipText={sectionTooltip}
      showRightLabel={showRightLabel}
    >
      {children}
      <RepeatField
        initialLength={list.length}
        key={list.length}
        maxSize={40}
        notification={notification}
        control={() => (
          <AddForm
            id={id}
            disabled={disabled}
            onAdd={pushItem}
            label={addLabel}
            currentPeopleOrDrivers={isAuto ? values.drivers : values.people}
            listLength={list.length}
            inOffer={inOffer}
            sppLimits={sppLimits}
            {...rest}
          />
        )}
      >
        {({ onRemove, focus }, i) => {
          const p = list[i];
          return (
            <Item
              fieldName={`${id}[${i}]`}
              fieldId={id}
              key={p?.id || i}
              index={i}
              item={p}
              onRemove={setIndexToRemove}
              removeFromList={onRemove}
              focus={focus}
              initialFold={!!folded[i]}
              onFold={toggleFold}
              disabled={disabled}
              previous={list[i - 1]}
              count={list.length}
              allowLicensedActions={allowLicensedActions}
              hasAuto={hasAuto}
              fromPolicy={fromPolicy}
              inOffer={inOffer}
              listLength={list.length}
              {...rest}
            />
          );
        }}
      </RepeatField>
    </Section>
  );
}

FormList.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string,
  item: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
  addForm: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
  addLabel: PropTypes.string,
  disabled: PropTypes.bool,
  singular: PropTypes.string,
  plural: PropTypes.string,
  children: PropTypes.node,
  type: PropTypes.string,
  classes: PropTypes.object,
  hasAuto: PropTypes.bool,
  fromPolicy: PropTypes.bool,
  inOffer: PropTypes.bool,
  sppLimits: PropTypes.object,
  sectionTooltip: PropTypes.string,
  onAdd: PropTypes.func
};

FormList.defaultProps = {
  title: 'People',
  disabled: false,
  singular: 'person',
  plural: 'people',
  addLabel: undefined,
  children: undefined,
  classes: {},
  type: 'SubSection',
  hasAuto: false,
  sectionTooltip: undefined
};

export default observer(FormList);
